import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { apiClient, type ChatHistoryResponse, type ChatAttachment } from '@/api/client';

export interface LocalAttachmentPreview {
  fileName: string;
  mimeType: string;
  dataUrl?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  source: 'mobile' | 'web';
  isStreaming?: boolean;
  attachments?: ChatAttachment[];
  localPreviews?: LocalAttachmentPreview[];
}

interface ChatState {
  sessionKey: string | null;
  chatSessionId: string | null;
  isOpen: boolean;
  isMinimized: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  isLoading: boolean;
  vehicle: { vin: string; year: string; make: string; model: string } | null;
  sessionCreatedAt: string | null;   // when the session this conversation belongs to was recorded (UTC)
}

interface ChatContextValue extends ChatState {
  openChat: (sessionKey: string) => Promise<void>;
  closeChat: () => void;
  minimizeChat: () => void;
  maximizeChat: () => void;
  sendMessage: (text: string, files?: File[]) => Promise<void>;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider');
  return ctx;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ChatState>({
    sessionKey: null,
    chatSessionId: null,
    isOpen: false,
    isMinimized: false,
    messages: [],
    isStreaming: false,
    isLoading: false,
    vehicle: null,
    sessionCreatedAt: null,
  });

  const abortRef = useRef<AbortController | null>(null);

  const openChat = useCallback(async (sessionKey: string) => {
    // If same session is already open, just maximize
    if (state.sessionKey === sessionKey && state.isOpen) {
      setState(s => ({ ...s, isMinimized: false }));
      return;
    }

    setState(s => ({ ...s, isLoading: true, isOpen: true, isMinimized: false, sessionKey }));

    try {
      const data: ChatHistoryResponse = await apiClient.getChatHistory(sessionKey);
      const messages: ChatMessage[] = data.messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
        source: m.source,
        attachments: m.attachments,
      }));

      setState(s => ({
        ...s,
        messages,
        vehicle: data.vehicle,
        sessionCreatedAt: data.sessionCreatedAt ?? null,
        chatSessionId: null, // New web chat session
        isLoading: false,
      }));
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setState(s => ({
        ...s,
        messages: [],
        vehicle: null,
      sessionCreatedAt: null,
        isLoading: false,
      }));
    }
  }, [state.sessionKey, state.isOpen]);

  const closeChat = useCallback(() => {
    abortRef.current?.abort();
    setState({
      sessionKey: null,
      chatSessionId: null,
      isOpen: false,
      isMinimized: false,
      messages: [],
      isStreaming: false,
      isLoading: false,
      vehicle: null,
      sessionCreatedAt: null,
    });
  }, []);

  const minimizeChat = useCallback(() => {
    setState(s => ({ ...s, isMinimized: true }));
  }, []);

  const maximizeChat = useCallback(() => {
    setState(s => ({ ...s, isMinimized: false }));
  }, []);

  const sendMessage = useCallback(async (text: string, files?: File[]) => {
    if (!state.sessionKey || state.isStreaming) return;

    let localPreviews: LocalAttachmentPreview[] | undefined;
    if (files && files.length > 0) {
      localPreviews = await Promise.all(files.map(async (f) => {
        const preview: LocalAttachmentPreview = { fileName: f.name, mimeType: f.type };
        if (f.type.startsWith('image/')) {
          preview.dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(f);
          });
        }
        return preview;
      }));
    }

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      source: 'web',
      localPreviews,
    };

    const streamingMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      source: 'web',
      isStreaming: true,
    };

    setState(s => ({
      ...s,
      messages: [...s.messages, userMsg, streamingMsg],
      isStreaming: true,
    }));

    try {
      const response = await apiClient.streamChatMessage(
        state.sessionKey,
        text,
        state.chatSessionId ?? undefined,
        files
      );

      if (!response.ok) {
        throw new Error(`Chat request failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let accumulated = '';
      let newChatSessionId = state.chatSessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'token') {
              accumulated += event.content;
              setState(s => ({
                ...s,
                messages: s.messages.map(m =>
                  m.id === streamingMsg.id
                    ? { ...m, content: accumulated }
                    : m
                ),
              }));
            } else if (event.type === 'done') {
              newChatSessionId = event.chatSessionId;
            } else if (event.type === 'error') {
              accumulated += `\n\n*Error: ${event.content}*`;
            }
            // tool_call and tool_result events are handled silently for now
          } catch {
            // Skip malformed SSE lines
          }
        }
      }

      setState(s => ({
        ...s,
        chatSessionId: newChatSessionId,
        isStreaming: false,
        messages: s.messages.map(m =>
          m.id === streamingMsg.id
            ? { ...m, content: accumulated, isStreaming: false }
            : m
        ),
      }));
    } catch (err) {
      console.error('Chat stream error:', err);
      setState(s => ({
        ...s,
        isStreaming: false,
        messages: s.messages.map(m =>
          m.id === streamingMsg.id
            ? { ...m, content: '{"markdown":"Sorry, something went wrong. Please try again.","followups":["Try again"]}', isStreaming: false }
            : m
        ),
      }));
    }
  }, [state.sessionKey, state.isStreaming, state.chatSessionId]);

  return (
    <ChatContext.Provider value={{
      ...state,
      openChat,
      closeChat,
      minimizeChat,
      maximizeChat,
      sendMessage,
    }}>
      {children}
    </ChatContext.Provider>
  );
}
